import { useState, useEffect, useRef } from "react";
import {
  Copy, ExternalLink, Save, Loader2,
  ChevronDown, ChevronUp, CheckCircle2, PauseCircle, PlayCircle,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabase";
import { useToast } from "../Toast";
import LeadCaptureSlugField, { isSlugValid } from "./LeadCaptureSlugField";
import LeadCaptureSettings from "./LeadCaptureSettings";
import LeadCaptureUtmBuilder from "./LeadCaptureUtmBuilder";
import LeadCaptureStats from "./LeadCaptureStats";

const DEFAULTS = {
  slug: "",
  is_active: true,
  title: "¿Quieres más información?",
  subtitle: "",
  primary_color: "#10b981",
  logo_url: null,
  show_servicio: true,
  show_como_conocio: true,
  require_phone: true,
  require_email: true,
  post_submit_mode: "thank_you",
  thank_you_message: "",
  redirect_url: "",
  notify_email: false,
  privacy_mode: "template",
  privacy_responsable: "",
  privacy_email: "",
  privacy_address: "",
  privacy_city: "",
  privacy_url: "",
};

function isPrivacyValid(v) {
  const mode = v.privacy_mode || "template";
  if (mode === "external") {
    return !!v.privacy_url?.trim();
  }
  return !!(
    v.privacy_responsable?.trim() &&
    v.privacy_email?.trim() &&
    v.privacy_address?.trim() &&
    v.privacy_city?.trim()
  );
}

function suggestSlug(email) {
  if (!email) return "";
  const base = email.split("@")[0]
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return base.slice(0, 30);
}

export default function LeadCaptureManager() {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState(null);
  const [values, setValues] = useState(DEFAULTS);
  const [editing, setEditing] = useState(false);
  const [justCreated, setJustCreated] = useState(false);
  const [confirmingPause, setConfirmingPause] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    supabase
      .from("lead_capture_forms")
      .select("*")
      .eq("student_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!alive) return;
        if (data) {
          setOriginal(data);
          setValues({
            ...DEFAULTS,
            ...data,
            post_submit_mode: data.redirect_url ? "redirect" : "thank_you",
            privacy_mode: data.privacy_url ? "external" : "template",
          });
        } else {
          setValues({ ...DEFAULTS, slug: suggestSlug(user.email) });
        }
        setLoading(false);
      });
    return () => { alive = false; };
  }, [user.id, user.email]);

  const publicUrl = values.slug
    ? `${window.location.origin}/l/${values.slug}`
    : "";
  const slugValid = isSlugValid(values.slug);
  const privacyOk = isPrivacyValid(values);
  const canSave = slugValid && privacyOk && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    const wasNew = !original;
    setSaving(true);
    try {
      const isRedirect = values.post_submit_mode === "redirect";
      const isExternalPrivacy = values.privacy_mode === "external";
      const payload = {
        student_id: user.id,
        slug: values.slug,
        is_active: values.is_active,
        title: values.title || DEFAULTS.title,
        subtitle: values.subtitle || null,
        primary_color: values.primary_color || DEFAULTS.primary_color,
        logo_url: values.logo_url || null,
        show_servicio: !!values.show_servicio,
        show_como_conocio: !!values.show_como_conocio,
        require_phone: !!values.require_phone,
        require_email: !!values.require_email,
        thank_you_message: isRedirect ? null : (values.thank_you_message || null),
        redirect_url: isRedirect ? (values.redirect_url || null) : null,
        notify_email: !!values.notify_email,
        notify_email_address: null,
        privacy_url: isExternalPrivacy ? (values.privacy_url || null) : null,
        privacy_responsable: values.privacy_responsable?.trim() || null,
        privacy_email:       values.privacy_email?.trim()       || null,
        privacy_address:     values.privacy_address?.trim()     || null,
        privacy_city:        values.privacy_city?.trim()        || null,
      };

      const { data, error } = await supabase
        .from("lead_capture_forms")
        .upsert(payload, { onConflict: "student_id" })
        .select()
        .single();

      if (error) throw error;
      setOriginal(data);
      setValues({
        ...DEFAULTS,
        ...data,
        post_submit_mode: data.redirect_url ? "redirect" : "thank_you",
        privacy_mode: data.privacy_url ? "external" : "template",
      });
      showSuccess(wasNew ? "Buzón creado" : "Buzón guardado");
      if (wasNew) {
        setEditing(false);
        setJustCreated(true);
        setTimeout(() => setJustCreated(false), 5000);
      }
      requestAnimationFrame(() => {
        const scroller = containerRef.current?.closest(".overflow-y-auto");
        scroller?.scrollTo({ top: 0, behavior: "smooth" });
      });
    } catch (err) {
      const msg = err?.message?.includes("unique")
        ? "Ese enlace ya lo tiene otro alumno"
        : err?.message || "No se pudo guardar el buzón";
      showError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      showSuccess("Enlace copiado");
    } catch {
      showError("No se pudo copiar");
    }
  };

  const handleToggleActive = async (next) => {
    setValues((v) => ({ ...v, is_active: next }));
    if (original) {
      const { error } = await supabase
        .from("lead_capture_forms")
        .update({ is_active: next })
        .eq("student_id", user.id);
      if (error) {
        setValues((v) => ({ ...v, is_active: !next }));
        showError("No se pudo cambiar el estado");
      } else {
        setOriginal({ ...original, is_active: next });
        showSuccess(next ? "Buzón activo" : "Buzón pausado");
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={20} className="animate-spin text-gray-400" />
      </div>
    );
  }

  const isActive = values.is_active;
  const showEditFields = !original || editing;

  return (
    <div ref={containerRef} className="space-y-5">
      {justCreated && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 animate-fade-in">
          <CheckCircle2 size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-emerald-800">¡Buzón creado!</p>
            <p className="text-xs text-emerald-700">
              Comparte tu enlace para empezar a recibir leads.
            </p>
          </div>
        </div>
      )}

      {original && (
        <>
          <div
            className={`p-3 rounded-lg border transition-colors ${
              confirmingPause
                ? "bg-amber-50 border-amber-200"
                : "bg-gray-50 border-gray-100"
            }`}
          >
            {!confirmingPause ? (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  {isActive ? (
                    <PlayCircle size={18} className="text-emerald-600 flex-shrink-0" />
                  ) : (
                    <PauseCircle size={18} className="text-gray-400 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {isActive ? "Buzón activo" : "Buzón pausado"}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {isActive
                        ? "Tu enlace recibe leads en este momento."
                        : "Tu enlace no acepta envíos."}
                    </p>
                  </div>
                </div>
                {isActive ? (
                  <button
                    onClick={() => setConfirmingPause(true)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-100 transition-colors flex-shrink-0"
                  >
                    Pausar
                  </button>
                ) : (
                  <button
                    onClick={() => handleToggleActive(true)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors flex-shrink-0"
                  >
                    Activar
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2.5">
                <p className="text-sm text-amber-900">
                  ¿Seguro que quieres pausar el buzón? Dejará de recibir leads
                  hasta que vuelvas a activarlo.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setConfirmingPause(false)}
                    className="flex-1 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-100 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => {
                      setConfirmingPause(false);
                      handleToggleActive(false);
                    }}
                    className="flex-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 transition-colors"
                  >
                    Sí, pausar
                  </button>
                </div>
              </div>
            )}
          </div>

          {publicUrl && (
            <div className="space-y-2">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                  Tu enlace
                </p>
                <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm">
                  <span className="flex-1 truncate text-gray-700">{publicUrl}</span>
                  <button
                    onClick={handleCopy}
                    className="p-1.5 hover:bg-gray-200 rounded-md transition-colors"
                    title="Copiar enlace"
                  >
                    <Copy size={14} className="text-gray-600" />
                  </button>
                  <a
                    href={publicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 hover:bg-gray-200 rounded-md transition-colors"
                    title="Abrir"
                  >
                    <ExternalLink size={14} className="text-gray-600" />
                  </a>
                </div>
              </div>
              <LeadCaptureUtmBuilder baseUrl={publicUrl} />

              {values.privacy_mode !== "external" && privacyOk && (
                <a
                  href={`/l/${values.slug}/privacidad`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 underline"
                >
                  Ver política de privacidad auto-generada
                </a>
              )}
            </div>
          )}

          <LeadCaptureStats
            viewsCount={original.views_count ?? 0}
            submissionsCount={original.submissions_count ?? 0}
          />

          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            className="w-full flex items-center justify-between text-sm text-gray-700 hover:text-gray-900 pt-3 border-t border-gray-100"
          >
            <span className="font-medium">Editar configuración</span>
            {editing ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </>
      )}

      {showEditFields && (
        <div className="space-y-4">
          <LeadCaptureSlugField
            value={values.slug}
            onChange={(slug) => setValues((v) => ({ ...v, slug }))}
            currentSlug={original?.slug}
          />

          <div className="pt-2">
            <LeadCaptureSettings values={values} onChange={setValues} />
          </div>

          <div className="flex flex-col gap-2 pt-3 border-t border-gray-100">
            {!privacyOk && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                {values.privacy_mode === "external"
                  ? "Pega el enlace a tu política de privacidad para poder guardar."
                  : "Para usar la plantilla de privacidad, rellena los 4 campos en «Opciones avanzadas → Política de privacidad»."}
              </p>
            )}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={handleSave}
                disabled={!canSave}
                className="flex items-center gap-2 px-4 py-2 bg-brand text-white text-sm font-medium rounded-lg hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                {original ? "Guardar cambios" : "Crear buzón"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
